import React, { useEffect, useState } from "react";
import axios from "../utils/api";
import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  TableContainer,
  Typography,
  Alert,
  Button,
  IconButton,
  CircularProgress,
  Box,
  Chip,
  Stack,
  Tooltip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate } from "react-router-dom";
import { Download as DownloadIcon } from "@mui/icons-material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import PendingActionsOutlinedIcon from "@mui/icons-material/PendingActionsOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import SessionDetailsDialog from "./DetailsView";

// Keyframes for loader
if (typeof document !== "undefined") {
  const styleId = "sessiondetails-loader-keyframes";
  if (!document.getElementById(styleId)) {
    const style = document.createElement("style");
    style.id = styleId;
    style.textContent = `
      @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
      @keyframes loading-bar {
        0% { transform: translateX(-60%); }
        50% { transform: translateX(10%); }
        100% { transform: translateX(120%); }
      }
    `;
    document.head.appendChild(style);
  }
}

const styles = {
  pageWrap: {
    minHeight: "100vh",
    background:
      "radial-gradient(1000px 520px at 4% -5%, rgba(124,58,237,.14), transparent 60%), radial-gradient(900px 480px at 98% 2%, rgba(168,85,247,.10), transparent 58%), #faf9ff",
    py: { xs: 2, md: 3.5 },
    px: { xs: 2, sm: 3, md: 4 },
    width: "100%",
    boxSizing: "border-box",
  },
  innerWrap: {
    width: "100%",
    maxWidth: 1320,
    margin: "0 auto",
    boxSizing: "border-box",
  },
  header: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: { xs: 1.5, sm: 2 },
    mb: 2.5,
    px: { xs: 2, md: 2.5 },
    py: { xs: 2, md: 2.25 },
    border: "1px solid rgba(148,163,184,.2)",
    borderRadius: 3,
    bgcolor: "rgba(255,255,255,.82)",
    backdropFilter: "blur(18px)",
    boxShadow: "0 10px 35px rgba(15,23,42,.06)",
  },
  backButton: {
    bgcolor: "white",
    border: "1px solid #e2e8f0",
    color: "#334155",
    boxShadow: "0 1px 2px rgba(15,23,42,.04)",
    "&:hover": {
      bgcolor: "#f5f3ff",
      color: "#7c3aed",
      borderColor: "#ddd6fe",
    },
  },
  title: {
    fontWeight: 800,
    color: "#0f172a",
    letterSpacing: "-0.025em",
    fontSize: { xs: "1.3rem", md: "1.65rem" },
  },
  subtitle: {
    color: "#64748b",
    fontSize: { xs: "0.78rem", sm: "0.875rem" },
    mt: 0.25,
  },
  downloadButton: {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 700,
    px: { xs: 1.25, sm: 2 },
    minWidth: { xs: 42, sm: "auto" },
    bgcolor: "#7c3aed",
    backgroundImage: "linear-gradient(135deg, #7c3aed 0%, #8b5cf6 100%)",
    boxShadow: "0 5px 14px rgba(124,58,237,.24)",
    "&:hover": {
      bgcolor: "#6d28d9",
      backgroundImage: "linear-gradient(135deg, #6d28d9 0%, #7c3aed 100%)",
      boxShadow: "0 7px 18px rgba(124,58,237,.3)",
    },
  },
  statCard: {
    flex: "1 1 210px",
    minWidth: { xs: "calc(50% - 6px)", sm: 180 },
    bgcolor: "rgba(255,255,255,.9)",
    border: "1px solid #e2e8f0",
    borderRadius: 2.5,
    px: { xs: 1.5, sm: 2 },
    py: 1.75,
    boxShadow: "0 6px 22px rgba(15,23,42,.045)",
    transition: "transform .2s ease, box-shadow .2s ease",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 10px 28px rgba(15,23,42,.08)",
    },
  },
};

const SessionsTable = () => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState({});
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchSessions = async () => {
      try {
        const response = await axios.get("/get-sessions");
        if (response.data.success) {
          setSessions(response.data.data);
        } else {
          setError("Failed to fetch sessions.");
        }
      } catch (err) {
        setError("An error occurred while fetching sessions.");
      } finally {
        setLoading(false);
      }
    };

    fetchSessions();
  }, []);

  const handleConfirm = async (sessionId) => {
    setLoadingStatus((prev) => ({ ...prev, [sessionId]: "confirming" }));
    try {
      const response = await axios.patch("/sessions-status", {
        sessionId,
        status: "conformed",
      });

      if (response.data.success) {
        setSessions((prevSessions) =>
          prevSessions.map((session) =>
            session._id === sessionId ? { ...session, status: "conformed" } : session
          )
        );
      }
    } catch (error) {
      console.error("Error confirming session:", error.message);
    } finally {
      setLoadingStatus((prev) => ({ ...prev, [sessionId]: "" }));
    }
  };

  const handleReject = async (sessionId) => {
    setLoadingStatus((prev) => ({ ...prev, [sessionId]: "rejecting" }));
    try {
      const response = await axios.patch("/sessions-status", {
        sessionId,
        status: "rejected",
      });

      if (response.data.success) {
        setSessions((prevSessions) =>
          prevSessions.map((session) =>
            session._id === sessionId ? { ...session, status: "rejected" } : session
          )
        );
      }
    } catch (error) {
      console.error("Error rejecting session:", error.message);
    } finally {
      setLoadingStatus((prev) => ({ ...prev, [sessionId]: "" }));
    }
  };

  const handleRowClick = (sessionId) => {
    setSelectedSessionId(sessionId);
  };

  const selectedSession = sessions.find(
    (session) => session._id === selectedSessionId
  );

  const downloadCSV = () => {
    const headers = [
      "Received Date",
      "Name",
      "Company Name",
      "Email",
      "Mobile",
      "Address",
      "Booking Date",
      "Status",
    ];
    const rows = sessions.map((session) => [
      new Date(session.createdAt).toLocaleDateString("en-GB"),
      session.name,
      session.companyName,
      session.email,
      session.mobileNumber,
      session.address,
      new Date(session.bookedDate).toLocaleDateString("en-GB"),
      session.status,
    ]);

    const csvContent =
      "data:text/csv;charset=utf-8," +
      [headers.join(","), ...rows.map((row) => row.join(","))].join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "sessions.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background:
            "radial-gradient(1200px 600px at 10% 0%, rgba(37, 99, 235, 0.10), rgba(255,255,255,0) 60%), radial-gradient(900px 450px at 90% 15%, rgba(16, 185, 129, 0.10), rgba(255,255,255,0) 55%), #ffffff",
          px: 2,
        }}
      >
        <Box
          sx={{
            position: "relative",
            width: 260,
            height: 260,
            borderRadius: "28px",
            bgcolor: "rgba(15,23,42,0.9)",
            boxShadow:
              "0 24px 60px rgba(15,23,42,0.55), 0 0 0 1px rgba(148,163,184,0.25)",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 3,
          }}
        >
          <Box
            sx={{
              position: "absolute",
              inset: "-40%",
              background:
                "conic-gradient(from 180deg at 50% 50%, rgba(56,189,248,0.2), rgba(129,140,248,0.35), rgba(56,189,248,0.2))",
              opacity: 0.8,
              filter: "blur(30px)",
              animation: "spin 12s linear infinite",
            }}
          />
          <Box
            sx={{
              position: "relative",
              width: 72,
              height: 72,
              borderRadius: "50%",
              bgcolor: "rgba(15,23,42,0.9)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow:
                "0 0 0 1px rgba(148,163,184,0.4), 0 0 0 12px rgba(15,23,42,0.9)",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                inset: -2,
                borderRadius: "50%",
                border: "3px solid transparent",
                borderTopColor: "#38bdf8",
                borderRightColor: "#6366f1",
                animation: "spin 1s linear infinite",
              }}
            />
            <Typography
              sx={{
                position: "relative",
                fontSize: 18,
                fontWeight: 700,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: "#e5e7eb",
              }}
            >
              S
            </Typography>
          </Box>
          <Box sx={{ position: "relative", textAlign: "center", px: 3 }}>
            <Typography
              sx={{
                fontSize: 16,
                fontWeight: 600,
                color: "#e5e7eb",
                mb: 0.5,
              }}
            >
              Loading sessions
            </Typography>
            <Typography sx={{ fontSize: 12, color: "#9ca3af" }}>
              Fetching booking details…
            </Typography>
          </Box>
          <Box
            sx={{
              position: "relative",
              width: "68%",
              height: 4,
              borderRadius: 999,
              bgcolor: "rgba(15,23,42,1)",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                background:
                  "linear-gradient(90deg, transparent, #38bdf8, #6366f1, transparent)",
                animation: "loading-bar 1.4s ease-in-out infinite",
              }}
            />
          </Box>
        </Box>
      </Box>
    );
  }

  const cellStyle = {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: "170px",
    borderBottom: "1px solid #eef2f7",
    padding: "14px 12px",
    fontSize: "13px",
    color: "#334155",
    textAlign: "left",
  };

  const statusCellStyle = {
    ...cellStyle,
    width: "210px",
    minWidth: "210px",
    maxWidth: "none",
    minHeight: "36px",
    overflow: "visible",
    textAlign: "center",
  };

  const headerStyle = {
    ...cellStyle,
    padding: "13px 12px",
    fontSize: "11px",
    fontWeight: 800,
    letterSpacing: "0.055em",
    textTransform: "uppercase",
    color: "#64748b",
    backgroundColor: "#f8fafc",
    borderBottom: "1px solid #e2e8f0",
  };

  const confirmedCount = sessions.filter(
    (session) => session.status === "conformed"
  ).length;
  const rejectedCount = sessions.filter(
    (session) => session.status === "rejected"
  ).length;
  const pendingCount = sessions.filter(
    (session) => session.status === "pending"
  ).length;

  const summaryCards = [
    {
      label: "Total sessions",
      value: sessions.length,
      icon: EventAvailableOutlinedIcon,
      iconColor: "#7c3aed",
      iconBg: "#f5f3ff",
    },
    {
      label: "Awaiting reply",
      value: pendingCount,
      icon: PendingActionsOutlinedIcon,
      iconColor: "#d97706",
      iconBg: "#fffbeb",
    },
    {
      label: "Confirmed",
      value: confirmedCount,
      icon: CheckCircleOutlineIcon,
      iconColor: "#059669",
      iconBg: "#ecfdf5",
    },
    {
      label: "Rejected",
      value: rejectedCount,
      icon: CancelOutlinedIcon,
      iconColor: "#dc2626",
      iconBg: "#fef2f2",
    },
  ];

  return (
    <Box sx={styles.pageWrap}>
      <Box sx={styles.innerWrap}>
        <Box sx={styles.header}>
          <Tooltip title="Go back">
            <IconButton
              onClick={() => navigate(-1)}
              size="medium"
              sx={styles.backButton}
              aria-label="Go back"
            >
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={styles.title}>Sessions list</Typography>
            <Typography sx={styles.subtitle}>
              View and manage all booking sessions
            </Typography>
          </Box>
          <Button
            onClick={downloadCSV}
            variant="contained"
            startIcon={<DownloadIcon />}
            sx={styles.downloadButton}
            aria-label="Export sessions as CSV"
          >
            <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
              Export CSV
            </Box>
          </Button>
        </Box>

        <Stack
          direction="row"
          useFlexGap
          flexWrap="wrap"
          spacing={1.5}
          sx={{ mb: 2.5 }}
        >
          {summaryCards.map(({ label, value, icon: StatIcon, iconColor, iconBg }) => (
            <Box key={label} sx={styles.statCard}>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Box
                  sx={{
                    width: 42,
                    height: 42,
                    borderRadius: 2,
                    display: "grid",
                    placeItems: "center",
                    color: iconColor,
                    bgcolor: iconBg,
                    flexShrink: 0,
                  }}
                >
                  <StatIcon fontSize="small" />
                </Box>
                <Box>
                  <Typography
                    sx={{ color: "#64748b", fontSize: 12, fontWeight: 600 }}
                  >
                    {label}
                  </Typography>
                  <Typography
                    sx={{ color: "#0f172a", fontSize: 22, fontWeight: 800, lineHeight: 1.2 }}
                  >
                    {value}
                  </Typography>
                </Box>
              </Stack>
            </Box>
          ))}
        </Stack>

        {error && (
          <Alert
            severity="error"
            sx={{ mb: 3, borderRadius: 2, border: "1px solid #fecaca" }}
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        {!error && (
        <TableContainer
          component={Paper}
          sx={{
            borderRadius: 3,
            overflowX: "auto",
            border: "1px solid #e2e8f0",
            bgcolor: "rgba(255,255,255,.94)",
            boxShadow: "0 12px 34px rgba(15,23,42,.06)",
          }}
        >
        <Table sx={{ minWidth: 1120 }}>
          <TableHead>
            <TableRow>
              <TableCell style={headerStyle}>Received Date</TableCell>
              <TableCell style={headerStyle}>Name</TableCell>
              <TableCell style={headerStyle}>Company Name</TableCell>
              <TableCell style={headerStyle}>Email</TableCell>
              <TableCell style={headerStyle}>Mobile</TableCell>
              <TableCell style={headerStyle}>Address</TableCell>
              <TableCell style={headerStyle}>Booking Date</TableCell>
              <TableCell style={headerStyle}>Reply Mail</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sessions.map((session) => (
              <TableRow
                key={session._id}
                hover
                onClick={() => handleRowClick(session._id)}
                sx={{
                  cursor: "pointer",
                  transition: "background-color .15s ease",
                  "&:last-child td": { borderBottom: 0 },
                  "&:hover": { bgcolor: "#f8fbff !important" },
                }}
              >
                <TableCell style={cellStyle}>
                  {new Date(session.createdAt).toLocaleDateString("en-GB")}
                </TableCell>
                <TableCell style={cellStyle}>{session.name}</TableCell>
                <TableCell style={cellStyle}>{session.companyName}</TableCell>
                <TableCell style={cellStyle}>{session.email}</TableCell>
                <TableCell style={cellStyle}>{session.mobileNumber}</TableCell>
                <TableCell style={cellStyle}>{session.address}</TableCell>
                <TableCell style={cellStyle}>
                  {new Date(session.bookedDate).toLocaleDateString("en-GB")}
                </TableCell>
                <TableCell style={statusCellStyle}>
                  {loadingStatus[session._id] === "confirming" ||
                  loadingStatus[session._id] === "rejecting" ? (
                    <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
                      <CircularProgress size={18} thickness={5} />
                      <Typography sx={{ fontSize: 12, color: "#64748b", fontWeight: 600 }}>
                        Updating
                      </Typography>
                    </Stack>
                  ) : session.status === "pending" ? (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 1,
                        whiteSpace: "nowrap",
                      }}
                    >
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<CheckCircleOutlineIcon sx={{ fontSize: "16px !important" }} />}
                        sx={{
                          minWidth: 92,
                          borderRadius: 1.75,
                          textTransform: "none",
                          fontWeight: 700,
                          backgroundImage:
                            "linear-gradient(to right, #3B82F6 0%, #EC4899 100%)",
                          boxShadow: "none",
                          "&:hover": {
                            backgroundImage:
                              "linear-gradient(to right, #2563eb 0%, #db2777 100%)",
                            boxShadow: "0 4px 12px rgba(99,102,241,.28)",
                          },
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleConfirm(session._id);
                        }}
                      >
                        Confirm
                      </Button>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<CancelOutlinedIcon sx={{ fontSize: "16px !important" }} />}
                        sx={{
                          minWidth: 84,
                          borderRadius: 1.75,
                          textTransform: "none",
                          fontWeight: 700,
                          color: "#b91c1c",
                          bgcolor: "#fee2e2",
                          boxShadow: "none",
                          "&:hover": {
                            bgcolor: "#fecaca",
                            boxShadow: "none",
                          },
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleReject(session._id);
                        }}
                      >
                        Reject
                      </Button>
                    </Box>
                  ) : session.status === "conformed" ? (
                    <Chip
                      icon={<CheckCircleOutlineIcon />}
                      label="Confirmed"
                      size="small"
                      sx={{
                        color: "#047857",
                        bgcolor: "#ecfdf5",
                        border: "1px solid #a7f3d0",
                        fontWeight: 700,
                        "& .MuiChip-icon": { color: "#059669" },
                      }}
                    />
                  ) : (
                    <Chip
                      icon={<CancelOutlinedIcon />}
                      label="Rejected"
                      size="small"
                      sx={{
                        color: "#b91c1c",
                        bgcolor: "#fef2f2",
                        border: "1px solid #fecaca",
                        fontWeight: 700,
                        "& .MuiChip-icon": { color: "#dc2626" },
                      }}
                    />
                  )}
                </TableCell>
              </TableRow>
            ))}
            {sessions.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} sx={{ border: 0, py: 8 }}>
                  <Stack alignItems="center" spacing={1.25}>
                    <Box
                      sx={{
                        width: 58,
                        height: 58,
                        borderRadius: "50%",
                        display: "grid",
                        placeItems: "center",
                        bgcolor: "#f5f3ff",
                        color: "#7c3aed",
                      }}
                    >
                      <EventAvailableOutlinedIcon />
                    </Box>
                    <Typography sx={{ fontWeight: 800, color: "#0f172a" }}>
                      No sessions yet
                    </Typography>
                    <Typography sx={{ color: "#64748b", fontSize: 13 }}>
                      New booking sessions will appear here.
                    </Typography>
                  </Stack>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
        )}
      </Box>
      <SessionDetailsDialog
        open={Boolean(selectedSession)}
        session={selectedSession}
        loadingStatus={
          selectedSession ? loadingStatus[selectedSession._id] : ""
        }
        onClose={() => setSelectedSessionId(null)}
        onConfirm={handleConfirm}
        onReject={handleReject}
      />
    </Box>
  );
};

export default SessionsTable;
